import { objects, strings } from 'investira.sdk';
import { getAtivosFilterConfig } from '../../../utils/ativosFilterRegistry';

/**
 * Informa se o valor representa a ausência de um filtro.
 *
 * @param {*} pValue Valor recebido da query.
 * @returns {boolean}
 */
function pvIsEmptyFilterValue(pValue) {
  return pValue === null || pValue === undefined || pValue === '';
}

/**
 * Normaliza um valor conforme o tipo declarado no contrato do parâmetro.
 *
 * @param {*} pValue Valor de entrada.
 * @param {Object} pDefinition Definição do parâmetro.
 * @returns {*}
 */
function pvNormalizeValue(pValue, pDefinition) {
  if (pvIsEmptyFilterValue(pValue)) {
    return null;
  }

  if (pDefinition.type === 'boolean') {
    if (pValue === true || pValue === 'true') {
      return true;
    }

    if (pValue === false || pValue === 'false') {
      return false;
    }

    return null;
  }

  if (pDefinition.type === 'number') {
    const xValue = Number(pValue);

    if (
      !Number.isFinite(xValue) ||
      (pDefinition.minimum !== undefined && xValue < pDefinition.minimum) ||
      (pDefinition.integer && !Number.isInteger(xValue))
    ) {
      return null;
    }

    return xValue;
  }

  if (pDefinition.type === 'csv') {
    const xValues = String(pValue)
      .replace(/%2C/gi, ',')
      .split(',')
      .map(pItem => pItem.trim())
      .filter(Boolean);

    return xValues.length ? xValues.join(',') : null;
  }

  if (pDefinition.type === 'enum') {
    return pDefinition.values.includes(pValue) ? pValue : null;
  }

  return pValue;
}

/**
 * Normaliza os parâmetros que pertencem a um contrato.
 *
 * @param {Object} pValues Valores recebidos.
 * @param {Object} pDefinitions Definições permitidas.
 * @returns {Object}
 */
function pvNormalizeParams(pValues, pDefinitions) {
  const xValues = {};

  Object.keys(pDefinitions).forEach(pParam => {
    const xValue = pvNormalizeValue(pValues[pParam], pDefinitions[pParam]);

    // `false` e `0` são mantidos quando aceitos pelo contrato do campo.
    if (!pvIsEmptyFilterValue(xValue)) {
      xValues[pParam] = xValue;
    }
  });

  return xValues;
}

/**
 * Restaura o estado dos controles a partir da query da URL.
 *
 * @param {string} pSearch Query string atual.
 * @param {string} pMercado Mercado efetivo.
 * @returns {Object}
 */
export function readAtivosFilterQuery(pSearch = '', pMercado) {
  const xQuery = strings.querystringToObject(pSearch) || {};
  const xConfig = getAtivosFilterConfig(pMercado);

  if (!xConfig) {
    return xQuery;
  }

  return pvNormalizeParams(xQuery, xConfig.queryParams);
}

/**
 * Atualiza o estado da query sem perder os demais filtros compatíveis.
 *
 * @param {Object} pQuery Estado atual.
 * @param {Object} pUpdates Alterações solicitadas.
 * @param {string} pMercado Mercado efetivo.
 * @returns {Object}
 */
export function updateAtivosFilterQuery(pQuery = {}, pUpdates = {}, pMercado) {
  const xQuery = {
    ...pQuery,
    ...pUpdates
  };
  const xConfig = getAtivosFilterConfig(pMercado);

  if (!xConfig) {
    return Object.keys(xQuery).reduce((pResult, pParam) => {
      if (!pvIsEmptyFilterValue(xQuery[pParam])) {
        pResult[pParam] = xQuery[pParam];
      }
      return pResult;
    }, {});
  }

  return pvNormalizeParams(xQuery, xConfig.queryParams);
}

/**
 * Serializa o estado dos controles para a URL.
 *
 * @param {Object} pQuery Estado atual dos controles.
 * @param {string} pMercado Mercado efetivo.
 * @returns {string}
 */
export function serializeAtivosFilterQuery(pQuery = {}, pMercado) {
  const xConfig = getAtivosFilterConfig(pMercado);

  if (!xConfig) {
    const xQuery = updateAtivosFilterQuery(pQuery, {}, pMercado);
    return Object.keys(xQuery).length ? objects.objectToQueryString(xQuery) : '';
  }

  const xQuery = pvNormalizeParams(pQuery, xConfig.queryParams);
  return Object.keys(xQuery).length ? objects.objectToQueryString(xQuery) : '';
}

/**
 * Remove o segmento de paginação antes de navegar após uma alteração de filtro.
 *
 * @param {string} pPathname Caminho atual.
 * @returns {string}
 */
export function removePaginationFromPath(pPathname = '') {
  return pPathname.replace(/\/page\/\d+$/, '');
}

/**
 * Saneia os parâmetros na fronteira de uma rota com contrato específico.
 * Mercados ainda não registrados preservam integralmente o comportamento atual.
 *
 * @param {Object} pParams Parâmetros montados pela paginação.
 * @param {string} pMercado Mercado resolvido pela rota.
 * @returns {Object}
 */
export function sanitizeAtivosListParams(pParams = {}, pMercado) {
  const xConfig = getAtivosFilterConfig(pMercado);

  // O fallback mantém a busca geral e mercados ainda não implementados.
  if (!xConfig) {
    return pParams;
  }

  const xParams = pvNormalizeParams(pParams, xConfig.requestParams);

  // O mercado da rota prevalece sobre qualquer valor manual da URL.
  xParams.mercado = xConfig.mercado;

  return xParams;
}
